import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { format, subDays } from 'date-fns';
import { apiGet, apiGetQuiet } from '../../shared/api/client';
import { isNative, isIOS } from '../../shared/utils/platform';
import { todayISO, localDateToUtcRange, isEventInLocalDay } from '../../shared/utils/dateHelpers';
import { useAppStore } from '../../shared/stores/appStore';
import { useSleepSchedule, getNextScheduleEvent } from './useSleepSchedule';
import { useOpenSleep } from './useOpenSleep';
import { expectedWakeTime } from './expectedWake';
import {
  getPrefs,
  subscribePrefs,
  addActionListener,
  summariseDay,
  rescheduleSweetSpots,
  rescheduleFeedReminder,
  rescheduleMedReminders,
  rescheduleVaccinationReminders,
  rescheduleSleepCheckIn,
  rescheduleTimerGuards,
  rescheduleNappyReminders,
  reschedulePumpReminder,
  rescheduleDailyRecap,
  rescheduleDailyTips,
  rescheduleGrowthCheckIn,
  rescheduleHealthReviews,
  rescheduleWellbeingCheckIn,
} from '../../services/notifications';
import * as liveActivity from '../../services/liveActivity';
import {
  ensureRegisteredOnLaunch,
  addPushActionListener,
  addPushReceivedListener,
} from '../../services/pushRegistration';
import { requestFullSync } from '../../services/sync/engine';

/**
 * The SINGLE subscriber that drives every sleep-suite service: local
 * notifications (services/notifications.js), the Live Activity, the
 * home-screen widget's data blob, launch push re-registration and
 * notification tap routing. Mounted exactly once in Layout (renders nothing)
 * so no scheduler ever runs twice.
 *
 * Everything is a silent no-op on web: `native` gates every scheduler, the
 * plugins are all resolved through dynamic `import()` inside try/catch, and
 * permission is NEVER requested here — only from an explicit toggle in
 * NotificationPreferences.
 *
 * Reschedule triggers:
 *  - schedule query success        → sweet spots, Live Activity, widget data
 *  - open sleep changes            → still-asleep check-in, timer guards,
 *                                    Live Activity sleeping mode
 *  - event data changes            → feed / nappy / pump reminders + recap
 *    (mutation sites invalidate the ['events'] prefix, which refetches the
 *    window query observed here — no per-form scheduler calls needed)
 *  - medication / vaccination / growth / mood data changes
 *  - app resume                    → full sync + query invalidation (and,
 *                                    after ≥ 10 min away, the plan and the
 *                                    baby-scoped status — v1.5.3)
 *  - preference changes            → subscribePrefs from notifications.js
 *
 * Last-event derivations (feed, nappy, pump) use a rolling LAST 24 HOURS
 * window rather than the local day, so a 01:00 feed still counts at 02:00
 * (docs/ux-modernisation-spec.md §9).
 */

const EMPTY = [];
const HOUR_MS = 60 * 60 * 1000;
const WINDOW_MS = 24 * HOUR_MS;
const LIVE_ACTIVITY_TICK_MS = 60 * 1000;
// v1.5.3 (docs/v153-spec.md §2.7): how long the app must have been in the
// background before a resume re-reads the plan and the baby-scoped answer —
// the paywall redirects read these (the first ask, the lapsed redirect, the
// second ask). Shorter absences (unlocking the phone mid-entry) never refresh
// them, so the dashboard is never pulled out from under a half-filled sheet.
const RESUME_STATUS_REFRESH_MS = 10 * 60 * 1000;
// Null-fields blob written when the widget must show nothing: losing Premium,
// and sign-out (which unmounts this driver). Without it the previous account's
// baby name and sleep state stay on the home screen indefinitely — the widget
// only ever re-reads this JSON, it never expires.
const EMPTY_WIDGET_DATA = {
  babyName: null,
  nextLabel: null,
  nextTimeISO: null,
  lastFeedISO: null,
  sleepingSinceISO: null,
};

// Module-level selectors so react-query can memoise between renders.
const selectRows = (res) => res?.items || res?.events || res || [];
const selectVaccinations = (res) => res?.vaccinations || res || [];
const selectGrowthRecords = (res) => res?.records || res || [];
const selectMoodEntries = (res) => res?.items || res || [];

const eventType = (e) => e?.entityType || e?.type;
const eventTs = (e) => e?.startTime || e?.createdAt || null;
const pumpTs = (e) => e?.endTime || e?.startTime || e?.createdAt || null;

/** Newest timestamp of `type` in `events`, as the raw ISO string (or null). */
function lastTimestamp(events, type, pick = eventTs) {
  let best = null;
  let bestMs = -Infinity;
  events.forEach((e) => {
    if (eventType(e) !== type) return;
    const value = pick(e);
    const ms = Date.parse(value);
    if (!Number.isFinite(ms) || ms <= bestMs) return;
    bestMs = ms;
    best = value;
  });
  return best;
}

export default function SleepSuiteServices() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { data: schedule, entitled, babyId, baby } = useSleepSchedule();
  const { openSleep } = useOpenSleep(babyId);
  const activeTimer = useAppStore((s) => s.activeTimer);
  const [prefs, setPrefs] = useState(getPrefs);

  useEffect(() => subscribePrefs(setPrefs), []);

  const babyName = baby?.name || 'Baby';
  const dob = baby?.dateOfBirth;
  const native = isNative();

  // --- Open sleep, reduced to primitives -------------------------------
  // `useOpenSleep` ticks once a second while a sleep is running, so nothing
  // downstream may depend on a freshly built object: every scheduler input is
  // derived from the start time and category alone.
  const openStartISO = openSleep?.startTime || null;
  const openCategory = openSleep?.data?.category || null;
  const openSleepInput = useMemo(
    () => (openStartISO ? { startTime: openStartISO, data: { category: openCategory } } : null),
    [openStartISO, openCategory]
  );

  // --- Last 24 hours of events -----------------------------------------
  const todayLocal = todayISO();
  const yesterdayLocal = useMemo(() => format(subDays(new Date(), 1), 'yyyy-MM-dd'), []);
  const range = useMemo(
    () => ({
      from: localDateToUtcRange(yesterdayLocal).from,
      to: localDateToUtcRange(todayLocal).to,
    }),
    [yesterdayLocal, todayLocal]
  );

  const { data: windowEvents = EMPTY } = useQuery({
    queryKey: ['events', babyId, 'window24', yesterdayLocal, todayLocal],
    queryFn: () => apiGet(`/babies/${babyId}/events`, { from: range.from, to: range.to }),
    enabled: !!babyId && native,
    select: selectRows,
  });

  const recentEvents = useMemo(() => {
    const cutoff = Date.now() - WINDOW_MS;
    return (windowEvents || []).filter((e) => {
      const ms = Date.parse(eventTs(e));
      return Number.isFinite(ms) && ms >= cutoff;
    });
  }, [windowEvents]);

  const lastFeedISO = useMemo(() => lastTimestamp(recentEvents, 'FEED'), [recentEvents]);
  const lastNappyISO = useMemo(() => lastTimestamp(recentEvents, 'NAPPY'), [recentEvents]);
  const lastPumpISO = useMemo(
    () => lastTimestamp(recentEvents, 'PUMPING', pumpTs),
    [recentEvents]
  );

  // Today's own slice, for the recap totals and the hydration count.
  const todayEvents = useMemo(
    () => (windowEvents || []).filter((e) => isEventInLocalDay(e, todayLocal)),
    [windowEvents, todayLocal]
  );
  const summary = useMemo(() => summariseDay(todayEvents, Date.now()), [todayEvents]);
  const wetTodayCount = useMemo(
    () =>
      todayEvents.filter((e) => {
        if (eventType(e) !== 'NAPPY') return false;
        const kind = e?.data?.type;
        return kind === 'wet' || kind === 'both';
      }).length,
    [todayEvents]
  );

  // --- Feature data, fetched only when its reminder is switched on ------
  const { data: medications = EMPTY } = useQuery({
    queryKey: ['medications', babyId],
    queryFn: () => apiGet(`/babies/${babyId}/medications`),
    enabled: !!babyId && native && !!prefs.reminders?.medications,
  });

  // Same key/shape as VaccinationPage so the cache entry is shared.
  const { data: vaccinations = EMPTY } = useQuery({
    queryKey: ['vaccinations', babyId],
    queryFn: () => apiGet(`/babies/${babyId}/vaccinations`),
    enabled: !!babyId && native && !!prefs.reminders?.vaccinations,
    select: selectVaccinations,
  });

  // Same key/endpoint as GrowthPage (['growth', babyId]), but read through the
  // toast-free client: GET /growth answers 403 "upgrade required" on the free
  // plan, and a background read must never throw that toast at app launch.
  // The check-in is only scheduled once the read has actually succeeded, so a
  // free user is never told "no measurements yet" about data we cannot see.
  const { data: growthRecords = EMPTY, isSuccess: growthLoaded } = useQuery({
    queryKey: ['growth', babyId],
    queryFn: () => apiGetQuiet(`/babies/${babyId}/growth`),
    enabled: !!babyId && native && !!prefs.reminders?.growth,
    select: selectGrowthRecords,
  });

  // Same key/endpoint as MoodPage (['mood']).
  const { data: moodEntries = EMPTY } = useQuery({
    queryKey: ['mood'],
    queryFn: () => apiGet('/mood', { days: 30 }),
    enabled: native && !!prefs.moodCheckIn,
    select: selectMoodEntries,
  });

  const moodLoggedToday = useMemo(() => {
    const today = new Date().toISOString().substring(0, 10);
    return (moodEntries || []).some((e) => e?.date === today);
  }, [moodEntries]);

  // --- Sweet spots (Premium) -------------------------------------------
  const sweetSpotsOn = entitled && !!prefs.napReminders;
  // `openSleepInput` (start + category, memoised) is part of the inputs: the
  // schedule cannot refresh offline, so the running sleep is what stops a
  // "bedtime sweet spot" from firing after the baby is already down.
  useEffect(() => {
    if (!native) return;
    rescheduleSweetSpots({ schedule, babyName, enabled: sweetSpotsOn, openSleep: openSleepInput });
  }, [native, schedule, sweetSpotsOn, babyName, openSleepInput]);

  // --- Live Activity + widget data (iOS, Premium) -----------------------
  // Priority: a sleep in progress shows the count-up "Asleep" activity; with
  // nothing asleep it falls back to the countdown to the next predicted nap.
  const avgNapMinutes = Number(schedule?.avgNapMinutes) || 0;
  const usualWakeTime = schedule?.usualWakeTime || null;
  const avgNightMinutes = Number(schedule?.avgNightMinutes) || 0;
  // One rule for the lock screen and both heroes (expectedWake.js): a nap
  // projects the usual nap length, a night sleep the usual MORNING wake
  // (v1.4.3 — `avgNapMinutes` is DAYTIME data; projecting it onto a night
  // sleep put "Usually wakes around 20:15" on the lock screen for a baby put
  // down at 19:30 and left it there all night). With nothing to go on the
  // target equals the start — the agreed "no expected wake time" signal
  // (syncSleeping → the Swift `expectedWake` guard returns nil).
  const expectedWakeISO = useMemo(() => {
    if (!openStartISO) return null;
    const wake = expectedWakeTime(
      { startTime: openStartISO, data: { category: openCategory } },
      { avgNapMinutes, usualWakeTime, avgNightMinutes }
    );
    return wake ? wake.toISOString() : openStartISO;
  }, [openStartISO, openCategory, avgNapMinutes, usualWakeTime, avgNightMinutes]);

  useEffect(() => {
    if (!native || !isIOS()) return;
    const next = getNextScheduleEvent(schedule);

    if (sweetSpotsOn && openStartISO && typeof liveActivity.syncSleeping === 'function') {
      liveActivity.syncSleeping({ babyName, startISO: openStartISO, expectedWakeISO });
    } else if (sweetSpotsOn && schedule) {
      liveActivity.syncFromSchedule(next, babyName);
    } else {
      liveActivity.endActivity();
    }

    // ALWAYS publish the current state — never gate on having data. The blob is
    // what the home-screen widget re-reads every 15 minutes, so a state we
    // decline to write is a state that stays on the home screen forever: with
    // `schedule` undefined (offline, a 5xx, or a cold launch) logging the wake
    // left "Asleep · since 13:05" counting up for an awake baby, with no way
    // back. Losing entitlement clears it for the same reason.
    if (entitled) {
      liveActivity.setWidgetData({
        babyName,
        nextLabel: next?.label || null,
        nextTimeISO: next?.time || null,
        lastFeedISO: lastFeedISO || null,
        sleepingSinceISO: openStartISO || null,
      });
    } else {
      liveActivity.setWidgetData(EMPTY_WIDGET_DATA);
    }
  }, [
    native,
    schedule,
    entitled,
    sweetSpotsOn,
    babyName,
    lastFeedISO,
    openStartISO,
    expectedWakeISO,
  ]);

  // Minute tick so the countdown Live Activity rolls over to the next event
  // and ends itself once bedtime passes while the app stays foregrounded.
  // The sleeping activity counts up natively, so it needs no tick.
  useEffect(() => {
    if (!native || !isIOS()) return undefined;
    if (!sweetSpotsOn || !schedule || openStartISO) return undefined;
    const interval = setInterval(() => {
      liveActivity.syncFromSchedule(getNextScheduleEvent(schedule), babyName);
    }, LIVE_ACTIVITY_TICK_MS);
    return () => clearInterval(interval);
  }, [native, schedule, sweetSpotsOn, babyName, openStartISO]);

  // --- Still-asleep check-in (Premium) ---------------------------------
  useEffect(() => {
    if (!native) return;
    rescheduleSleepCheckIn({
      openSleep: openSleepInput,
      avgNapMinutes,
      babyName,
      enabled: entitled && !!prefs.sleepCheckIn,
    });
  }, [native, openSleepInput, avgNapMinutes, babyName, entitled, prefs.sleepCheckIn]);

  // --- Timer-left-running guards ---------------------------------------
  useEffect(() => {
    if (!native) return;
    rescheduleTimerGuards({
      openSleep: openSleepInput,
      activeTimer,
      babyName,
      enabled: !!prefs.timerGuards,
    });
  }, [native, openSleepInput, activeTimer, babyName, prefs.timerGuards]);

  // --- Feed reminder ----------------------------------------------------
  useEffect(() => {
    if (!native) return;
    rescheduleFeedReminder({
      lastFeedISO,
      enabled: !!prefs.reminders?.feeds,
      minutes: prefs.feedReminderMinutes,
      babyName,
    });
  }, [native, lastFeedISO, prefs.reminders?.feeds, prefs.feedReminderMinutes, babyName]);

  // --- Nappy gap + afternoon hydration nudge (opt-in) -------------------
  useEffect(() => {
    if (!native) return;
    rescheduleNappyReminders({
      lastNappyISO,
      wetTodayCount,
      dob,
      babyName,
      enabled: !!prefs.reminders?.nappies,
    });
  }, [native, lastNappyISO, wetTodayCount, dob, babyName, prefs.reminders?.nappies]);

  // --- Pumping reminder (opt-in) ---------------------------------------
  useEffect(() => {
    if (!native) return;
    reschedulePumpReminder({
      lastPumpISO,
      enabled: !!prefs.reminders?.pumping,
      minutes: prefs.pumpReminderMinutes,
    });
  }, [native, lastPumpISO, prefs.reminders?.pumping, prefs.pumpReminderMinutes]);

  // --- Medication dose reminders ---------------------------------------
  useEffect(() => {
    if (!native) return;
    rescheduleMedReminders({ medications, enabled: !!prefs.reminders?.medications });
  }, [native, medications, prefs.reminders?.medications]);

  // --- Vaccination due reminders + NHS health-review heads-up ----------
  // Both live under the one "Vaccinations and health reviews" toggle.
  const vaccinationsOn = !!prefs.reminders?.vaccinations;
  useEffect(() => {
    if (!native) return;
    rescheduleVaccinationReminders({ dob, vaccinations, enabled: vaccinationsOn, babyName });
  }, [native, dob, vaccinations, vaccinationsOn, babyName]);

  useEffect(() => {
    if (!native) return;
    rescheduleHealthReviews({ dob, babyName, enabled: vaccinationsOn });
  }, [native, dob, babyName, vaccinationsOn]);

  // --- Growth check-in --------------------------------------------------
  useEffect(() => {
    if (!native) return;
    rescheduleGrowthCheckIn({
      records: growthRecords,
      dob,
      babyId,
      babyName,
      enabled: !!prefs.reminders?.growth && growthLoaded,
    });
  }, [native, growthRecords, growthLoaded, dob, babyId, babyName, prefs.reminders?.growth]);

  // --- Daily evening recap ---------------------------------------------
  useEffect(() => {
    if (!native) return;
    rescheduleDailyRecap({
      enabled: !!prefs.dailyRecap?.enabled,
      time: prefs.dailyRecap?.time,
      feeds: summary.feeds,
      nappies: summary.nappies,
      sleepMinutes: summary.sleepMinutes,
      babyName,
      schedule: entitled ? schedule : null,
    });
  }, [
    native,
    prefs.dailyRecap?.enabled,
    prefs.dailyRecap?.time,
    summary,
    babyName,
    entitled,
    schedule,
  ]);

  // --- Daily tip (7 days pre-scheduled) --------------------------------
  useEffect(() => {
    if (!native) return;
    rescheduleDailyTips({
      enabled: !!prefs.dailyTip?.enabled,
      time: prefs.dailyTip?.time,
      dob,
      babyId,
    });
  }, [native, prefs.dailyTip?.enabled, prefs.dailyTip?.time, dob, babyId]);

  // --- Weekly wellbeing check-in (opt-in) ------------------------------
  useEffect(() => {
    if (!native) return;
    rescheduleWellbeingCheckIn({ enabled: !!prefs.moodCheckIn, moodLoggedToday });
  }, [native, prefs.moodCheckIn, moodLoggedToday]);

  // End the Live Activity AND clear the widget blob when this driver unmounts
  // (sign-out tears down Layout) so the previous account's baby name never
  // lingers on the lock screen or the home screen after logout.
  useEffect(() => {
    return () => {
      liveActivity.endActivity();
      liveActivity.setWidgetData(EMPTY_WIDGET_DATA);
    };
  }, []);

  // --- Navigation from a notification tap ------------------------------
  const navRef = useRef(navigate);
  useEffect(() => {
    navRef.current = navigate;
  }, [navigate]);

  const goTo = useCallback((route) => {
    if (typeof route !== 'string' || !route) return;
    try {
      navRef.current(route);
    } catch {
      /* router unavailable — a tap just opens the app */
    }
  }, []);

  // --- Once per app run: resume + tap listeners + push re-registration --
  const initialized = useRef(false);
  useEffect(() => {
    if (initialized.current || !native) return undefined;
    initialized.current = true;

    if (isIOS()) ensureRegisteredOnLaunch();

    let disposed = false;
    const cleanups = [];
    const keep = (off) => {
      if (disposed) {
        try {
          off();
        } catch {
          /* already gone */
        }
        return;
      }
      cleanups.push(off);
    };

    // App resume: pull anything logged on another device, then refresh the
    // queries every scheduler reads.
    (async () => {
      try {
        const { App } = await import('@capacitor/app');
        // v1.5.3: when the app last went to the background — null until it
        // has (first run), and again once a resume has used it.
        let pausedAt = null;
        const pauseHandle = await App.addListener('pause', () => {
          pausedAt = Date.now();
        });
        keep(() => pauseHandle.remove());
        const handle = await App.addListener('resume', () => {
          requestFullSync();
          queryClient.invalidateQueries({ queryKey: ['sleep-schedule'] });
          queryClient.invalidateQueries({ queryKey: ['events'] });
          queryClient.invalidateQueries({ queryKey: ['medications'] });
          queryClient.invalidateQueries({ queryKey: ['vaccinations'] });
          queryClient.invalidateQueries({ queryKey: ['growth'] });
          queryClient.invalidateQueries({ queryKey: ['mood'] });
          // v1.5.3: the paywall redirects read these. Only after a real
          // absence (RESUME_STATUS_REFRESH_MS): a parent who reopens the app
          // the next morning gets the first ask (and the lapsed and second-ask
          // redirects) without a cold start, while unlocking the phone
          // mid-entry never pulls the dashboard out from under a half-filled
          // sheet. No recorded pause → no refresh.
          const awayMs = pausedAt === null ? null : Date.now() - pausedAt;
          pausedAt = null;
          if (awayMs !== null && awayMs >= RESUME_STATUS_REFRESH_MS) {
            queryClient.invalidateQueries({ queryKey: ['subscription-status'] });
            queryClient.invalidateQueries({ queryKey: ['entitlement'] });
          }
        });
        keep(() => handle.remove());
      } catch {
        /* App plugin unavailable — resume refresh just won't happen */
      }
    })();

    // Local notification taps → extra.route.
    (async () => {
      try {
        const off = await addActionListener((route) => goTo(route));
        keep(off);
      } catch {
        /* listener unavailable — taps just open the app */
      }
    })();

    // Caregiver pushes: refresh on arrival, deep-link on tap. Both helpers
    // load @capacitor/push-notifications through a guarded dynamic import and
    // keep at most one native listener per event.
    (async () => {
      try {
        const off = await addPushReceivedListener(() => {
          requestFullSync();
        });
        keep(off);
      } catch {
        /* plugin not installed — silent */
      }
    })();
    (async () => {
      try {
        const off = await addPushActionListener((route) => {
          requestFullSync();
          goTo(route);
        });
        keep(off);
      } catch {
        /* plugin not installed — silent */
      }
    })();

    return () => {
      disposed = true;
      initialized.current = false;
      cleanups.forEach((off) => {
        try {
          off();
        } catch {
          /* already removed */
        }
      });
      cleanups.length = 0;
    };
  }, [native, queryClient, goTo]);

  return null;
}
